import 'react-toastify/dist/ReactToastify.css';

import { APP_NAME, queryClient } from '@basqet-dashboard/common/config';
import { UserProvider } from '@basqet-dashboard/common/context';
import { useSessionStorage } from '@basqet-dashboard/common/hooks';
import { FLAGS, setTheme } from '@basqet-dashboard/common/utils';
import posthog from 'posthog-js';
import { PostHogProvider, useFeatureFlagEnabled } from 'posthog-js/react';
import React from 'react';
import { useAuth } from 'react-oidc-context';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { Dashboard } from './layouts';
import { ErrorPage } from './pages/404';
import {
  Callback,
  ForgotPassword,
  Login,
  Onboarding,
  ResetPassword,
  Signup,
  TwoFactorCode,
  VerifyEmail
} from './pages/auth';

function App() {
  setTheme();

  return (
    <PostHogProvider client={posthog}>
      <AppShell />
    </PostHogProvider>
  );
}

function AppShell() {
  const auth = useAuth();
  const merchantOidcFlagEnabled = useFeatureFlagEnabled(FLAGS.MERCHANT_OIDC);
  const merchantOidcEnabled = merchantOidcFlagEnabled === true;

  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider oidcUser={merchantOidcEnabled ? auth.user : null}>
        <Router>
          <Switch>
            <AuthRoute path="/login" component={Login}></AuthRoute>
            {merchantOidcFlagEnabled !== false && (
              <Route path="/callback" component={Callback}></Route>
            )}
            <AuthRoute path="/onboarding/:token" component={Onboarding}></AuthRoute>
            <AuthRoute path="/signup/:step?" component={Signup}></AuthRoute>
            <AuthRoute path="/verification" component={VerifyEmail}></AuthRoute>
            <AuthRoute path="/forgot-password" component={ForgotPassword}></AuthRoute>
            <AuthRoute path="/reset-password" component={ResetPassword}></AuthRoute>
            <SecureRoute path="/secure" component={TwoFactorCode}></SecureRoute>
            <ProtectedRoute path="/" component={Dashboard}></ProtectedRoute>
            <Route path="*" component={ErrorPage}></Route>
          </Switch>
        </Router>
      </UserProvider>
      <ToastContainer
        theme="dark"
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
      />
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}

const SecureRoute = ({ ...props }) => {
  const auth = useAuth();
  const merchantOidcEnabled = useFeatureFlagEnabled(FLAGS.MERCHANT_OIDC) === true;
  const [session] = useSessionStorage(APP_NAME, '');
  const { merchantUser, token } = session || {};

  if (merchantOidcEnabled) {
    return auth.isAuthenticated ? <Route {...props} /> : <Redirect to="/login" />;
  }

  return !merchantUser && token ? <Route {...props} /> : <Redirect to="/login" />;
};
const ProtectedRoute = ({ ...props }) => {
  const auth = useAuth();
  const merchantOidcEnabled = useFeatureFlagEnabled(FLAGS.MERCHANT_OIDC) === true;
  const [session] = useSessionStorage(APP_NAME, '');
  const { merchantUser, token } = session || {};

  if (merchantOidcEnabled) {
    return auth.isAuthenticated ? <Route {...props} /> : <Redirect to="/login" />;
  }

  return merchantUser && token ? <Route {...props} /> : <Redirect to="/login" />;
};
const AuthRoute = ({ ...props }) => {
  const auth = useAuth();
  const merchantOidcEnabled = useFeatureFlagEnabled(FLAGS.MERCHANT_OIDC) === true;
  const [session] = useSessionStorage(APP_NAME, '');
  const { merchantUser } = session || {};

  if (merchantOidcEnabled) {
    return !auth.isAuthenticated ? <Route {...props} /> : <Redirect to="/" />;
  }

  return !session || !merchantUser ? <Route {...props} /> : <Redirect to="/" />;
};
export default App;
